import type { FC, MouseEvent } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import SelectOutlined from 'src/assets/svg/select_outlined.svg?react';
import SelectOutlinedUnchecked from 'src/assets/svg/select_outlined_unchecked.svg?react';
import { SECTION } from 'src/constants/section';
import type { Module } from 'src/types/modulePermission';
import type {
  ModuleOrPlanActionOrgUnit,
  PlanActionPermissionOrgUnit,
} from 'src/types/orgUnit';
import type { OnSelectProps } from 'src/components/common/OrgUnitModal';
import OrgUnitModal from 'src/components/common/OrgUnitModal';

import { styles } from './index.styles';

export interface OrgUnitSelectorProps {
  disallowSelectionFor: any;
  index: number;
  module: Module;
  onRemove: (params: any) => void;
  onSelect: (index: number, orgUnit: any) => void;
  orgUnit?: ModuleOrPlanActionOrgUnit;
}

export const ORG_UNIT_FIELD_TEST_ID = 'org-unit-field';
export const INCLUDE_SUBNODES_CHECKBOX_TEST_ID = 'include-subnodes-checkbox';
export const MANAGER_IN_CHARGE_CHECKBOX_TEST_ID = 'manager-in-charge-checkbox';

const OrgUnitSelector: FC<OrgUnitSelectorProps> = ({
  disallowSelectionFor,
  index,
  module,
  onRemove,
  onSelect,
  orgUnit = {} as ModuleOrPlanActionOrgUnit,
}) => {
  const { t } = useTranslation('admin');
  const [showModal, setShowModal] = useState(false);
  const isEmpty = !Object.keys(orgUnit).length;
  const orgUnitTitle = !isEmpty ? `${orgUnit.org_unit_id} ${orgUnit.name}` : '';
  const hasResponsibleManagerField = module.module_type === SECTION.PLAN_ACTION;
  const hasReadOnlyField = module.module_type === SECTION.PLAN_ACTION;
  const orgUnitType =
    module.id === SECTION.PLAN_ACTION
      ? 'PlanActionPermissionOrgUnit'
      : 'ModulePermissionOrgUnit';

  // TODO: Get these module dependent fields in a more dynamic and reliable way at some point.
  const defaultFieldValues = {
    include_subnodes: false,
    is_manager_in_charge: hasResponsibleManagerField ? false : undefined,
    module_permission_org_unit_type: orgUnitType,
    read_only: hasReadOnlyField ? false : undefined,
  };

  const handleSelect = ({ orgUnit }: OnSelectProps) => {
    !!onSelect &&
      onSelect(index, {
        ...orgUnit,
        ...defaultFieldValues,
      });
  };

  const handleRemove = (index: number) => (event: MouseEvent) => {
    if (!isEmpty) {
      event.preventDefault();
      event.stopPropagation();

      !!onRemove && onRemove(index);
    }
  };

  const handleCheckbox = (index: number) => (event: any) => {
    const {
      target: { name: checkboxName, checked },
    } = event;

    onSelect(index, {
      ...orgUnit,
      [checkboxName]: checked,
    });
  };

  return (
    <Box sx={styles.orgUnitSelectorWrapper}>
      <Box sx={styles.orgUnitSelector}>
        <Box
          sx={styles.orgUnitSelectorLabel}
          data-test-id={ORG_UNIT_FIELD_TEST_ID}
          onClick={() => setShowModal(true)}
        >
          <Typography sx={{ fontWeight: 600, minWidth: '120px' }}>
            OrgUnit
          </Typography>

          <Box sx={styles.inputWrapper}>
            <TextField
              fullWidth
              name="orgUnitTitle"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <KeyboardArrowDownIcon />
                  </InputAdornment>
                ),
              }}
              placeholder={t('Permission.SearchPlaceholder')}
              value={orgUnitTitle}
              sx={styles.input}
            />
            <Box>{orgUnit.business_unit && `BU: ${orgUnit.business_unit}`}</Box>
          </Box>
        </Box>
        <AccountTreeOutlinedIcon
          sx={styles.orgTreeIcon}
          onClick={() => setShowModal(true)}
        />
        <Box sx={styles.iconWrapper}>
          {!hasResponsibleManagerField && !hasReadOnlyField && (
            <IconButton
              data-test-id={`remove-org-unit-${index}`}
              onClick={handleRemove(index)}
              disabled={isEmpty}
            >
              <DeleteOutlineOutlinedIcon />
            </IconButton>
          )}
        </Box>
      </Box>
      <FormGroup>
        <Stack>
          <FormControlLabel
            sx={{ mt: '-1rem' }}
            data-test-id={INCLUDE_SUBNODES_CHECKBOX_TEST_ID}
            control={
              <Checkbox
                size="medium"
                icon={
                  <SelectOutlinedUnchecked width="1.5rem" height="1.5rem" />
                }
                checkedIcon={<SelectOutlined width="1.5rem" height="1.5rem" />}
                checked={orgUnit?.include_subnodes || false}
                disabled={isEmpty}
                name="include_subnodes"
                onChange={handleCheckbox(index)}
                aria-label={t('Permission.IncludeSubnodes')}
                disableRipple
              />
            }
            label={t('Permission.IncludeSubnodes')}
          />
          {hasResponsibleManagerField && (
            <FormControlLabel
              sx={{ mt: '-0.625rem' }}
              data-test-id={MANAGER_IN_CHARGE_CHECKBOX_TEST_ID}
              control={
                <Checkbox
                  size="medium"
                  icon={
                    <SelectOutlinedUnchecked width="1.5rem" height="1.5rem" />
                  }
                  checkedIcon={
                    <SelectOutlined width="1.5rem" height="1.5rem" />
                  }
                  checked={
                    (orgUnit as PlanActionPermissionOrgUnit)
                      .is_manager_in_charge || false
                  }
                  disabled={isEmpty}
                  name="is_manager_in_charge"
                  onChange={handleCheckbox(index)}
                  disableRipple
                />
              }
              label={t('Permission.IsResponsibleManager')}
            />
          )}
          {hasReadOnlyField && (
            <FormControlLabel
              sx={{ mt: '-0.625rem' }}
              control={
                <Checkbox
                  size="medium"
                  icon={
                    <SelectOutlinedUnchecked width="1.5rem" height="1.5rem" />
                  }
                  checkedIcon={
                    <SelectOutlined width="1.5rem" height="1.5rem" />
                  }
                  checked={
                    (orgUnit as PlanActionPermissionOrgUnit).read_only || false
                  }
                  disabled={isEmpty}
                  name="read_only"
                  onChange={handleCheckbox(index)}
                  disableRipple
                />
              }
              label={t('Permission.ReadOnly')}
            />
          )}
        </Stack>
      </FormGroup>
      <OrgUnitModal
        disallowSelectionFor={disallowSelectionFor}
        moduleId={module.id}
        onClose={() => setShowModal(false)}
        onSelect={handleSelect}
        showModal={showModal}
        useAdminUserPermissions
      />
    </Box>
  );
};

export default OrgUnitSelector;
